import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, Button } from '@cbrebuild/blocks';

import LoadingBar from '../../nucleus/loading-bar/loading-bar';
import RowErrorBoundary from '../../components/error-boundary/row-error-boundary';
import { formatDealTypeAndRole, formatDealStage, getUserName, getLeadProducerName } from '../../utils/deal-display-utils';
import * as adminService from '../../services/admin-service';

const AdminRestoreDealDetails = ({
  deal,
  expandedDealId,
  formatDate,
  auditTrailMap,
  auditLoading,
  showOnlyContent = false,
  onRestore,
  onLoadAudit
}) => {
  // State for audit expansion within the details
  const [showAuditHistory, setShowAuditHistory] = useState(false);

  const idKey = deal.deal_iq_id || deal.id || deal.deal_pk || deal.pk;

  return (
    <RowErrorBoundary>
      <div className="expanded-details">
        <div className="deal-card-unified">
            {/* Header Section with Deal Name, Property Info and Status */}
            <div className="card-header" style={{ marginBottom: '24px' }}>
              <div className="header-layout admin-header-layout-relative">
                <div className="deal-title-section">
                  <h3 className="deal-title" style={{ color: '#009A44' }}>{deal.deal_name}</h3>

                </div>

                {/* Status indicator and restore action */}
                <div className="deal-status-indicator" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                  <span
                    className={`status-badge ${deal.is_deleted ? 'deleted' : 'active'}`}
                    style={deal.is_deleted ? {
                      border: '1px solid #ee8484',
                      padding: '4px 8px',
                      borderRadius: '1px',
                      backgroundColor: '#ee848433',
                      color: '#c73636',
                      fontWeight: 'bold',
                      fontSize: '12px'
                    } : { fontSize: '12px', padding: '6px 8px', borderRadius: '1px', backgroundColor: '#d3f2e0ff', color: '#009A44', fontWeight: '600' }}
                  >
                    {deal.is_deleted ? 'DELETED' : 'ACTIVE'}
                  </span>
                  {deal.is_deleted && onRestore && (
                    <Button
                      onClick={() => onRestore(deal)}
                      style={{
                        backgroundColor: '#009A44',
                        color: '#fff',
                        padding: '6px 8px',
                        fontSize: '12px',
                        borderRadius: '1px',
                        boxShadow: '0 0 0 1px #009A44',
                        border: 'none'
                      }}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            </div>          {/* Deal Details Grid */}
          <div className="card-content admin-reduced-spacing">
            <div className="deal-info-grid">
              {/* Basic Deal Information */}
              <div className="info-item">
                <div className="info-label">DEAL IQ ID</div>
                <div className="info-value">{deal.deal_iq_id || deal.id || '--'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">MTA ID</div>
                <div className="info-value">{deal.mta_deal_id || '--'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">VOUCHER UNIQUE ID</div>
                <div className="info-value">{deal.voucher_unique_id || '--'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">PURSUIT ID</div>
                <div className="info-value">{deal.pursuit_id || 'Pending'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">CONNECTOR ID</div>
                <div className="info-value">{deal.connector_id || '--'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">DEAL TYPE</div>
                <div className="info-value">{formatDealTypeAndRole(deal.deal_type, deal.rep_role)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">DEAL STAGE</div>
                <div className="info-value">{formatDealStage(deal.deal_life_cycle_stage)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">CREATED BY</div>
                <div className="info-value">
                  {typeof deal.created_by === 'string'
                    ? deal.created_by
                    : getUserName(deal.created_by) || '--'}
                  {(deal.created_by?.work_email || (deal.created_by?.email?.work)) && (
                    <span className="lead-producer-email-card">
                      <svg className="lead-producer-envelope" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.5 5.5A2.5 2.5 0 0 1 5 3h10a2.5 2.5 0 0 1 2.5 2.5v9A2.5 2.5 0 0 1 15 17H5a2.5 2.5 0 0 1-2.5-2.5v-9Zm2.1.5 5.4 4.5 5.4-4.5" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="lead-producer-email-text">{deal.created_by?.work_email || deal.created_by?.email?.work || '--'}</span>
                    </span>
                  )}
                </div>
              </div>
              {/* <div className="info-item">
                <div className="info-label">Is DELETED</div>
                <div className="info-value">
                  {deal.is_deleted ? 'Yes' : 'No'}
                </div>
              </div> */}
              <div className="info-item">
                <div className="info-label">CREATED DATE</div>
                <div className="info-value">{formatDate(deal.created_date)}</div>
              </div>
            </div>

            {/* Linked MTA Transactions Section */}
            <div className="linked-mta-transactions-section">
              <div className="linked-mta-header">
                <span className="linked-mta-label">LINKED MTA TRANSACTIONS</span>
              </div>
              {deal.linked_mta_transactions && deal.linked_mta_transactions.length > 0 ? (
                <div className="info-value">
                  {deal.linked_mta_transactions.map(tx => `${tx.transaction_id} - ${tx.deal_id}`).join(', ')}
                </div>
              ) : (
                <div className="linked-mta-no-transactions">
                  <span className="no-transactions-text">--</span>
                </div>
              )}
            </div>

            {/* Audit History Section */}
            <div className="audit-history-section">
              <div className="linked-mta-header">
                <span className="linked-mta-label" style={{ fontSize: '14px', fontWeight: '600', color: '#333', fontFamily: 'inherit' }}>AUDIT HISTORY</span>
              </div>

              <div style={{ marginTop: '12px', marginBottom: '12px', display: 'flex', gap: '8px' }}>
                <Button
                  size="sm"
                  theme="primary"
                  onClick={() => {
                    // Always refresh audit when opening
                    if (!showAuditHistory) {
                      onLoadAudit(deal);
                    }
                    setShowAuditHistory(!showAuditHistory);
                  }}
                >
                  {showAuditHistory ? 'Hide' : 'View'} History
                </Button>
              </div>

              {/* Show audit history when expanded */}
              {showAuditHistory && (
                <div className="collaborators-subsection">
                  {auditLoading ? (
                    <div className="audit-loading">
                      <LoadingBar />
                      <span>Loading audit history...</span>
                    </div>
                  ) : !(auditTrailMap[idKey] || []).length ? (
                    <div className="audit-empty">No audit history found</div>
                  ) : (
                    <div className="audit-table" style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', padding:'24px' }}>
                      <div className="admin-table-header">
                        <div className="admin-header-cell" style={{backgroundColor: '#526061', color:'white'}}>MODIFIED TIMESTAMP</div>
                        <div className="admin-header-cell" style={{backgroundColor: '#526061', color:'white'}}>MESSAGE</div>
                        <div className="admin-header-cell" style={{backgroundColor: '#526061', color:'white'}}>MODIFIED BY</div>
                      </div>
                      <div className="audit-body">
                        {(auditTrailMap[idKey] || []).map((entry, idx) => (
                          <div key={idx} className="admin-table-row">
                            <div className="admin-cell">{entry.modified_timestamp || entry.timestamp ? new Date(entry.modified_timestamp || entry.timestamp).toLocaleString() : '--'}</div>
                            <div className="admin-cell" title={entry.message}>{entry.message || entry.action || '--'}</div>
                            <div className="admin-cell">{entry.user_email || entry.email || (entry.user && entry.user.email) || '--'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </RowErrorBoundary>
  );
};

AdminRestoreDealDetails.propTypes = {
  deal: PropTypes.shape({
    deal_iq_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deal_name: PropTypes.string,
    properties: PropTypes.array,
    is_deleted: PropTypes.bool,
    voucher_unique_id: PropTypes.string,
    pursuit_id: PropTypes.string,
    connector_id: PropTypes.string,
    deal_type: PropTypes.string,
    rep_role: PropTypes.string,
    deal_life_cycle_stage: PropTypes.string,
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    lead_producer: PropTypes.object,
    created_date: PropTypes.string,
    linked_mta_transactions: PropTypes.array,
  }).isRequired,
  expandedDealId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formatDate: PropTypes.func.isRequired,
  auditTrailMap: PropTypes.object,
  auditLoading: PropTypes.bool,
  onRestore: PropTypes.func,
  onLoadAudit: PropTypes.func.isRequired,
};

export default AdminRestoreDealDetails;